// Pricing Settings Store - Uses Supabase Edge Functions
import { supabase } from '@/lib/supabase';

export interface PricingSettings {
  base_delivery_fee: { amount: number; currency: string; label: string };
  free_delivery_threshold: { amount: number; currency: string; label: string };
  vat_rate: { rate: number; name: string; label: string };
  min_order_amount: { amount: number; currency: string; label: string };
  max_delivery_distance: { distance: number; unit: string; label: string };
}

export interface DeliveryZone {
  id: string; name: string; branch_id: string | null;
  min_distance_km: number; max_distance_km: number; fee: number;
  is_active: boolean; sort_order: number; created_at: string; updated_at: string;
}

export interface DeliveryZoneInput {
  name: string; branch_id?: string | null; min_distance_km: number;
  max_distance_km: number; fee: number; is_active?: boolean; sort_order?: number;
}

const DEFAULT_SETTINGS: PricingSettings = {
  base_delivery_fee: { amount: 300, currency: 'KES', label: 'Base Delivery Fee' },
  free_delivery_threshold: { amount: 0, currency: 'KES', label: 'Free Delivery Threshold' },
  vat_rate: { rate: 0, name: 'VAT', label: 'VAT Rate' },
  min_order_amount: { amount: 0, currency: 'KES', label: 'Minimum Order Amount' },
  max_delivery_distance: { distance: 15, unit: 'km', label: 'Max Delivery Distance' }
};

let settingsCache: PricingSettings | null = null;
let zonesCache: DeliveryZone[] | null = null;

async function getAuthHeader() {
  if (!supabase) return {};
  const session = await supabase.auth.getSession();
  return session.data.session?.access_token ? { 'Authorization': `Bearer ${session.data.session.access_token}` } : {};
}

export const pricingStore = {
  async getSettings(forceRefresh = false): Promise<PricingSettings> {
    if (settingsCache && !forceRefresh) return settingsCache;
    try {
      const { data, error } = await supabase!.functions.invoke('pricing-settings');
      if (error) throw error;
      if (data?.data) { settingsCache = data.data; return data.data; }
      return DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  },

  async updateSettings(settings: Partial<PricingSettings>): Promise<boolean> {
    try {
      const headers = await getAuthHeader();
      const { data, error } = await supabase!.functions.invoke('pricing-settings', {
        body: settings,
      });
      if (error) throw error;
      if (data?.data) { settingsCache = null; return true; }
      return false;
    } catch { return false; }
  },

  async getZones(forceRefresh = false): Promise<DeliveryZone[]> {
    if (zonesCache && !forceRefresh) return zonesCache;
    try {
      const { data, error } = await supabase!.functions.invoke('pricing-zones');
      if (error) throw error;
      if (data?.data) { zonesCache = data.data; return data.data; }
      return [];
    } catch { return []; }
  },

  async createZone(zone: DeliveryZoneInput): Promise<DeliveryZone | null> {
    try {
      const { data, error } = await supabase!.functions.invoke('pricing-zones', {
        body: zone,
      });
      if (error) throw error;
      if (data?.data) { zonesCache = null; return data.data; }
      return null;
    } catch { return null; }
  },

  async updateZone(id: string, updates: Partial<DeliveryZoneInput>): Promise<boolean> {
    try {
      const { data, error } = await supabase!.functions.invoke('pricing-zones', {
        body: { id, ...updates },
      });
      if (error) throw error;
      if (data?.data) { zonesCache = null; return true; }
      return false;
    } catch { return false; }
  },

  async deleteZone(id: string): Promise<boolean> {
    try {
      const { data, error } = await supabase!.functions.invoke('pricing-zones', {
        body: { id },
      });
      if (error) throw error;
      if (data?.data || data?.message) { zonesCache = null; return true; }
      return false;
    } catch { return false; }
  },

  async getDeliveryFeeForDistance(distanceKm: number, subtotal = 0): Promise<{ fee: number; isFree: boolean; zone: string; threshold: number }> {
    try {
      const { data, error } = await supabase!.functions.invoke('pricing-calculate', {
        body: { distance_km: distanceKm },
      });
      if (error) throw error;
      if (data?.data) {
        return { fee: data.data.total_fee || data.data.delivery_fee, isFree: data.data.is_free_delivery || false, zone: data.data.zone || 'Standard', threshold: data.data.free_delivery_threshold || 0 };
      }
      return { fee: 0, isFree: subtotal >= 0, zone: 'Standard', threshold: 0 };
    } catch { return { fee: 0, isFree: subtotal >= 0, zone: 'Standard', threshold: 0 }; }
  },

  clearCache() { settingsCache = null; zonesCache = null; }
};
